import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { BookOpen, Flame, Target, ListChecks, ArrowRight, Sparkle } from "@phosphor-icons/react";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip } from "recharts";

const MOOD_VAL = { happy: 5, calm: 4, anxious: 3, sad: 2, angry: 1 };
const MOOD_COLOR = { happy: "#E8B059", calm: "#8FA998", sad: "#7A8B99", angry: "#C27E67", anxious: "#B89C82" };

function nextBestAction(data) {
  if (data.entries_count === 0) return "Write your first entry — even one line counts.";
  if (data.streak === 0) return "Reignite your streak with a 1-minute reflection.";
  return "You're on a roll. Keep the streak alive.";
}

export default function Dashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [today, setToday] = useState(null);

  useEffect(() => {
    let mounted = true;
    Promise.all([api.get("/dashboard"), api.get("/journey/today")])
      .then(([d, t]) => {
        if (!mounted) return;
        setData(d.data); setToday(t.data);
      })
      .catch(() => {});
    return () => { mounted = false; };
  }, []);

  if (!data || !today) {
    return <div className="h-64 flex items-center"><div className="w-2 h-2 rounded-full bg-moss animate-breathe" /></div>;
  }

  const moodSeries = [...(data.recent_moods || [])].reverse().map((m, i) => ({
    idx: i + 1,
    value: (MOOD_VAL[m.mood] || 3) * (m.intensity / 10),
    mood: m.mood,
  }));

  return (
    <div className="space-y-10">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="text-xs tracking-[0.3em] uppercase text-sage font-bold mb-2">
            Day {data.current_day} of 90 · {data.progress_pct}% through
          </p>
          <h1 className="font-serif text-4xl md:text-5xl font-medium">Hello, {user?.name?.split(" ")[0] || "friend"}.</h1>
        </div>
        <Link to="/app/journal" data-testid="dashboard-write-today"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-moss text-white hover:bg-mossDark transition-all">
          Write today's entry <ArrowRight size={16} />
        </Link>
      </div>

      {/* Today prompt featured */}
      <div className="relative bg-white rounded-3xl border border-border p-8 md:p-10 soft-shadow overflow-hidden">
        <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full bg-clay/15 blur-3xl"></div>
        <div className="relative">
          <p className="text-xs tracking-[0.3em] uppercase text-sage font-bold mb-4 flex items-center gap-2">
            <Sparkle size={14} weight="duotone" /> Today's prompt
          </p>
          <p className="font-serif text-2xl md:text-4xl leading-snug max-w-3xl">
            {today.ai_prompt || today.prompt}
          </p>
          <Link to="/app/journal" data-testid="dashboard-prompt-cta"
                className="inline-flex items-center gap-2 mt-6 px-5 py-2.5 rounded-full border border-border hover:bg-cream transition-all">
            Begin writing <ArrowRight size={16} />
          </Link>
        </div>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
        <StatCard label="Streak" value={`${data.streak} day${data.streak === 1 ? "" : "s"}`} icon={Flame} testid="stat-streak" />
        <StatCard label="Entries" value={data.entries_count} icon={BookOpen} testid="stat-entries" />
        <StatCard label="Open tasks" value={data.open_todos} icon={ListChecks} testid="stat-todos" />
        <StatCard label="Goal progress" value={`${data.goal_completion}%`} icon={Target} testid="stat-goals" />
      </div>

      {/* Mood + journey progress */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white rounded-3xl border border-border p-6 md:p-8 soft-shadow">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-serif text-xl">Mood weather</h3>
            <Link to="/app/mood" className="text-xs text-moss hover:underline" data-testid="link-mood">Log mood</Link>
          </div>
          {moodSeries.length === 0 ? (
            <div className="h-48 flex flex-col items-center justify-center text-center text-muted-foreground">
              <p className="mb-3">No mood check-ins yet.</p>
              <Link to="/app/mood" data-testid="empty-mood-cta"
                    className="px-4 py-2 rounded-full bg-moss/10 text-moss text-sm">Log how you feel</Link>
            </div>
          ) : (
            <div className="h-48">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={moodSeries}>
                  <defs>
                    <linearGradient id="g1" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#5B7B61" stopOpacity={0.45} />
                      <stop offset="100%" stopColor="#5B7B61" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <XAxis dataKey="idx" hide />
                  <YAxis hide domain={[0, 5]} />
                  <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #D8D4CC", background: "#fff" }}
                           formatter={(v, n, p) => [p.payload.mood, "Mood"]} labelFormatter={() => ""}/>
                  <Area type="monotone" dataKey="value" stroke="#5B7B61" strokeWidth={2.5} fill="url(#g1)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>

        <div className="bg-white rounded-3xl border border-border p-6 md:p-8 soft-shadow">
          <h3 className="font-serif text-xl mb-4">Your 90-day arc</h3>
          <div className="relative h-3 rounded-full bg-cream overflow-hidden mb-3">
            <div className="absolute inset-y-0 left-0 bg-gradient-to-r from-moss to-sage rounded-full"
                 style={{ width: `${data.progress_pct}%` }} />
          </div>
          <p className="text-sm text-muted-foreground">
            Day <span className="text-charcoal font-medium">{data.current_day}</span> of 90.
            <br/>
            {90 - data.current_day} day{90 - data.current_day === 1 ? "" : "s"} remaining in your journey.
          </p>
          <div className="mt-6 p-4 rounded-2xl bg-cream/60 text-sm">
            <p className="text-xs tracking-[0.2em] uppercase text-sage font-bold mb-1">Next best action</p>
            <p>{data.entries_count === 0 ? "Write your first entry — even one line counts." : data.streak === 0 ? "Reignite your streak with a 1-minute reflection." : "You're on a roll. Keep the streak alive."}</p>
          </div>
          <Link to="/app/book" data-testid="dashboard-book-cta"
                className="mt-5 inline-flex items-center justify-center gap-2 w-full px-4 py-3 rounded-full bg-clay text-white hover:bg-clay/90 text-sm transition-all">
            Book a 1:1 with Angela <ArrowRight size={14} />
          </Link>
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value, icon: Icon, testid }) {
  return (
    <div className="bg-white rounded-3xl border border-border p-6 soft-shadow" data-testid={testid}>
      <div className="flex items-center justify-between mb-4">
        <p className="text-xs tracking-[0.2em] uppercase text-sage font-bold">{label}</p>
        <Icon size={18} weight="duotone" className="text-moss" />
      </div>
      <p className="font-serif text-3xl font-medium">{value}</p>
    </div>
  );
}
